import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { createChart, ColorType, CrosshairMode, CandlestickSeries, HistogramSeries, LineSeries } from 'lightweight-charts';
import apiService from '../../services/apiService';
import { useToast } from '../../components/Toast';
import { ArrowLeft, Search, ChevronDown, ChevronUp, Settings, RefreshCw, X, Download } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Indicators from '../../utils/stockIndicators';

// Global Style Reset
const GlobalStyle = createGlobalStyle`
  body, html {
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden;
    background-color: #0a0a0a;
  }
  #root {
    margin: 0 !important;
    padding: 0 !important;
    height: 100vh;
  }
`;

// --- Styled Components (Preserved & Optimized) ---
const FullScreenContainer = styled.div`
  position: fixed;
  top: 60px; /* Assume header height is ~60px */
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  background: #0a0a0a;
  color: #e5e5e5;
  font-family: 'Pretendard', sans-serif;
  display: flex;
  flex-direction: column;
  z-index: 100;
`;

const Header = styled.div`
  height: 40px;
  background: rgba(20, 20, 20, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  z-index: 10;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h1`
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
`;

const ChartContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const Legend = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  font-size: 12px;
  font-family: 'Roboto Mono', monospace;
  pointer-events: none;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const LegText = styled.span`
  color: ${props => props.color || '#fff'};
`;

const FloatingPanel = styled.div`
  position: absolute;
  top: 50px;
  right: 12px;
  width: 260px;
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  z-index: 20;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  display: ${props => props.visible ? 'block' : 'none'};
`;

const PanelTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ControlGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 11px;
  color: #888;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  background: #222;
  border: 1px solid #444;
  color: #fff;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  &:focus { outline: none; border-color: #0072ff; }
`;

const SearchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 250px;
  background: #222;
  border: 1px solid #444;
  border-radius: 4px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 50;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
`;

const HistoryModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const HistoryModalContent = styled.div`
  background: #1e1e1e;
  border: 1px solid #444;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,0.8);
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #333;
  
  h3 {
    margin: 0;
    font-size: 14px;
    color: #fff;
  }
`;

const HistoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 16px;
  overflow-y: auto;
`;

const HistoryCard = styled.div`
  background: #2a2a2a;
  border: 1px solid #333;
  padding: 10px;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #3a3a3a;
  }

  span {
    font-size: 13px;
    color: #ddd;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SearchHistoryItem = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  color: #ddd;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    background: #333;
  }
`;

const DeleteHistoryBtn = styled.button`
  background: transparent;
  border: none;
  color: #888;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  
  &:hover {
    color: #ff5252;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 4px 0;
`;

const ToggleLabel = styled.span`
  font-size: 12px;
  color: #ddd;
`;

const Switch = styled.button`
  width: 36px;
  height: 20px;
  background: ${props => props.active ? '#0072ff' : '#444'};
  border-radius: 10px;
  border: none;
  position: relative;
  cursor: pointer;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '18px' : '2px'};
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: left 0.2s;
  }
`;

const TimeframeBar = styled.div`
  display: flex;
  gap: 2px;
  background: #111;
  padding: 4px;
  border-bottom: 1px solid #222;
`;

const TFButton = styled.button`
  background: ${props => props.active ? '#333' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#888'};
  border: none;
  padding: 4px 8px;
  font-size: 11px;
  border-radius: 3px;
  cursor: pointer;
  &:hover { color: #fff; background: #222; }
`;

const TechnicalPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // Added
  const { showToast } = useToast();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  // Series Refs (to update data without re-creating)
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const indicatorSeriesRefs = useRef({}); // { sma20: series, ... }

  // State - Initialize from URL params if available
  const [symbol, setSymbol] = useState(searchParams.get('symbol') || '005930');
  const [period, setPeriod] = useState(searchParams.get('period') || 'D');
  const [panelVisible, setPanelVisible] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [ohclData, setOhclData] = useState([]);
  const ohclDataRef = useRef([]); // Ref for access in event listeners
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMoreData, setNoMoreData] = useState(false);
  const [isPending, setIsPending] = useState(false); // Flag for background data generation
  const [isSyncing, setIsSyncing] = useState(false); // Added for manual sync
  const noMoreDataRef = useRef(false); // Ref for access in event listeners
  const loadingMoreRef = useRef(false); // Ref for immediate access in event callback
  const loadMoreDataRef = useRef(null); // Ref to hold latest loadMoreData function

  const [stockName, setStockName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchingApi, setIsSearchingApi] = useState(false);

  // Search History State (now stores objects {code, name})
  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('stockSearchHistory');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Helper to add to search history
  const saveSearchHistory = (stockObj) => {
    if (!stockObj) return;
    const stockCode = typeof stockObj === 'string' ? stockObj : stockObj.code;
    const stockName = typeof stockObj === 'string' ? stockObj : stockObj.name;
    if (!stockCode) return;

    setSearchHistory(prev => {
      // Remove if already exists to move it to the front, handling both legacy strings and new objects
      const filtered = prev.filter(item => {
        const itemCode = typeof item === 'string' ? item : (item.code || '');
        const itemName = typeof item === 'string' ? item : (item.name || '');
        return itemCode !== stockCode && itemCode !== stockName && itemName !== stockCode && itemName !== stockName;
      });

      const newObj = typeof stockObj === 'string' ? { code: stockObj, name: stockObj } : stockObj;
      const newHistory = [newObj, ...filtered].slice(0, 20); // Keep top 20
      localStorage.setItem('stockSearchHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  };

  const removeSearchHistory = (e, codeToRemove) => {
    e.stopPropagation(); // prevent clicking the item
    setSearchHistory(prev => {
      const filtered = prev.filter(item => {
        const itemCode = typeof item === 'string' ? item : item.code;
        return itemCode !== codeToRemove && item !== codeToRemove;
      });
      localStorage.setItem('stockSearchHistory', JSON.stringify(filtered));
      return filtered;
    });
  };

  // Live Search Effect (Debounced)
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        return;
      }
      setIsSearchingApi(true);
      try {
        const results = await apiService.searchStocks(searchQuery.trim());
        setSearchResults(results || []);
      } catch (err) {
        console.error('Search API error:', err);
        setSearchResults([]);
      } finally {
        setIsSearchingApi(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchSearchResults();
    }, 100);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Auto Refresh State
  const [isAutoRefresh, setIsAutoRefresh] = useState(false);

  // Indicator Config State
  const [config, setConfig] = useState(() => {
    const savedConfig = localStorage.getItem('chartIndicatorConfig');
    let parsedConfig = null;
    if (savedConfig) {
      try {
        parsedConfig = JSON.parse(savedConfig);
        // Migration from old sma20/sma60 to ma20/ma60
        if (parsedConfig.sma20) { parsedConfig.ma20 = parsedConfig.sma20; delete parsedConfig.sma20; }
        if (parsedConfig.sma60) { parsedConfig.ma60 = parsedConfig.sma60; delete parsedConfig.sma60; }
        // Ensure ma200 exists
        if (!parsedConfig.ma200) { parsedConfig.ma200 = { enabled: false, period: 200, color: '#ff5252', lineWidth: 1, type: 'SMA' }; }
        // Ensure tbmSuperTrend exists
        if (!parsedConfig.tbmSuperTrend) { parsedConfig.tbmSuperTrend = { enabled: false, period: 10, multiplier: 3, upColor: '#00e676', downColor: '#ff5252', lineWidth: 2 }; }
      } catch (e) {
        console.error('Failed to parse saved chart config:', e);
      }
    }
    // Default Config (or merged from localstorage)
    return parsedConfig || {
      ma20: { enabled: true, period: 20, color: '#fbc02d', lineWidth: 1, type: 'SMA' },
      ma60: { enabled: true, period: 60, color: '#00e676', lineWidth: 1, type: 'SMA' },
      ma200: { enabled: false, period: 200, color: '#ff5252', lineWidth: 1, type: 'SMA' },
      tbmSuperTrend: { enabled: false, period: 10, multiplier: 3, upColor: '#00e676', downColor: '#ff5252', lineWidth: 2 },
      bb: { enabled: false, period: 20, stdDev: 2, color: '#e040fb', lineWidth: 1 },
      vwap20: { enabled: false, period: 20, color: '#ff9800', lineWidth: 1, type: 'VWAP' },
      vwap72: { enabled: false, period: 72, color: '#00d4ff', lineWidth: 1, type: 'VWAP' },
      rsi: { enabled: false, period: 14, color: '#2962FF', lineWidth: 1 }
    };
  });

  // Save config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('chartIndicatorConfig', JSON.stringify(config));
  }, [config]);

  // Legend State
  const [legendData, setLegendData] = useState({ open: 0, high: 0, low: 0, close: 0, change: 0, percent: 0 });

  // --- 1. Initialize Chart ---
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0a0a0a' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: 'rgba(42, 46, 57, 0.2)' },
        horzLines: { color: 'rgba(42, 46, 57, 0.2)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      rightPriceScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
        scaleMargins: {
          top: 0.1,
          bottom: 0.2, // Leave space for volume
        },
      },
      // lightweight-charts v5는 기본 UTC 표시 → 서울(KST, UTC+9) 기준으로 포맷
      localization: {
        timeFormatter: (time) => {
          if (typeof time === 'number') {
            // 분봉: Unix timestamp (UTC seconds) → KST로 변환해 표시
            return new Date(time * 1000).toLocaleString('ko-KR', {
              timeZone: 'Asia/Seoul',
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false,
            });
          }
          // 일봉: BusinessDay 객체 { year, month, day }
          if (time && typeof time === 'object') {
            return `${time.year}/${String(time.month).padStart(2, '0')}/${String(time.day).padStart(2, '0')}`;
          }
          return String(time);
        },
      },
      timeScale: {
        borderColor: 'rgba(197, 203, 206, 0.3)',
        timeVisible: true,
      },
      height: chartContainerRef.current.clientHeight, // Initial height
    });

    console.log('Chart created', chart);
    chartRef.current = chart;

    // Add Candle Series
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#ef5350',
      downColor: '#26a69a',
      borderVisible: false,
      wickUpColor: '#ef5350',
      wickDownColor: '#26a69a',
    });
    candleSeriesRef.current = candlestickSeries;

    // Add Volume Series
    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: '#26a69a',
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '', // Overlay on same scale but minimized
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    });
    volumeSeriesRef.current = volumeSeries;

    // Resize Handler
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight
        });
      }
    };

    window.addEventListener('resize', handleResize);
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(chartContainerRef.current);

    // Crosshair Move Handler (Legend Update)
    chart.subscribeCrosshairMove(param => {
      if (param.time) {
        const price = param.seriesData.get(candlestickSeries);
        if (price) {
          const open = price.open;
          const close = price.close;
          const change = close - open;
          const percent = (change / open) * 100;
          setLegendData({
            open, high: price.high, low: price.low, close,
            change, percent
          });
        }
      }
    });

    // Subscribe to Visible Time Range Change
    const handleVisibleRangeChange = (range) => {
      if (!range) return;
      console.log(`[VisibleRange] from: ${range.from}, to: ${range.to}`);
      if (range.from < 10) {
        // Call the latest version via ref
        if (loadMoreDataRef.current) {
          loadMoreDataRef.current();
        }
      }
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      chart.remove();
    };
  }, []);


  // Sync state with refs
  useEffect(() => {
    ohclDataRef.current = ohclData;
  }, [ohclData]);

  useEffect(() => {
    noMoreDataRef.current = noMoreData;
  }, [noMoreData]);


  // --- 2. Fetch Data (Initial) ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    setNoMoreData(false);
    setStockName(''); // Clear old name to prevent mismatch during load

    // Fetch Name (Non-blocking) via new Info API
    apiService.getStockInfo(symbol).then(res => {
      console.log('[TechnicalPage] Stock Info:', res);
      if (res && res.name) setStockName(res.name);
    }).catch(err => console.error("Failed to fetch stock info", err));

    try {
      const candles = await apiService.getStockCandles(symbol, period, 'stock');
      console.log(`[TechnicalPage] Fetched ${period} candles:`, candles ? candles.length : 0);

      if (candles && candles.pending) {
        showToast(candles.message, 'info');
        setOhclData([]); // Clear chart
        setIsPending(true);
        return;
      }
      setIsPending(false);

      if (!candles || candles.length === 0) {
        showToast('No data found for symbol', 'error');
        setOhclData([]); // Clear chart
        return;
      }

      const formattedData = transformData(candles);
      console.log('[TechnicalPage] Formatted Data Sample:', formattedData.slice(0, 3));

      // Remove duplicates
      const uniqueData = [...new Map(formattedData.map(item => [item['time'], item])).values()];

      console.log('Fetched Data:', uniqueData);
      setOhclData(uniqueData);

      if (candleSeriesRef.current) {
        candleSeriesRef.current.setData(uniqueData);
      }

      if (volumeSeriesRef.current) {
        const volData = uniqueData.map(d => ({
          time: d.time,
          value: d.volume,
          color: d.close >= d.open ? 'rgba(239, 83, 80, 0.5)' : 'rgba(38, 166, 154, 0.5)'
        }));
        volumeSeriesRef.current.setData(volData);
      }

      // Force fit content to show data
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();

        // Apply time scale options based on period
        const isIntraday = ['1', '5', '15', '30', '60'].includes(period);
        chartRef.current.applyOptions({
          timeScale: {
            timeVisible: isIntraday,
            secondsVisible: false,
          }
        });
      }

      // Initial Legend
      if (uniqueData.length > 0) {
        updateLegend(uniqueData[uniqueData.length - 1]);
      }

    } catch (e) {
      console.error('Data Fetch Error:', e);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [symbol, period, showToast]);

  // --- 2c. Quiet Refresh (Auto/Manual) ---
  const refreshData = useCallback(async () => {
    try {
      const candles = await apiService.getStockCandles(symbol, period, 'stock');
      if (!candles || candles.length === 0) return;

      const uniqueData = transformData(candles); // Transform handles all sorting and deduplication

      setOhclData(uniqueData);

      if (candleSeriesRef.current) {
        candleSeriesRef.current.setData(uniqueData);
      }

      if (volumeSeriesRef.current) {
        const volData = uniqueData.map(d => ({
          time: d.time,
          value: d.volume,
          color: d.close >= d.open ? 'rgba(239, 83, 80, 0.5)' : 'rgba(38, 166, 154, 0.5)'
        }));
        volumeSeriesRef.current.setData(volData);
      }

      // Update Legend with Latest
      if (uniqueData.length > 0) {
        updateLegend(uniqueData[uniqueData.length - 1]);
      }
    } catch (e) {
      console.error('Refresh Data Error:', e);
    }
  }, [symbol, period]);

  // --- 2e. Manual Market Sync ---
  const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);

    // 분봉(intraday)일 때는 현재 period만, 일봉/주봉/월봉은 D/W/M 묶음으로 수집
    const isIntraday = ['1', '5', '15', '60', '240'].includes(period);
    const periodsToSync = isIntraday ? [period] : ['D', 'W', 'M'];

    showToast(`${symbol} ${isIntraday ? period + '분봉' : '일/주/월봉'} 동기화 중...`, 'info');
    try {
      // Step 1: Tell backend to fetch fresh data from KIS and update Firestore
      const res = await apiService.collectStockData(symbol, periodsToSync);
      console.log('[TechnicalPage] Sync Result:', res);

      // Step 2: Show per-period result summary
      const summary = Object.entries(res.results || {})
        .map(([p, r]) => r.error ? `${p}:오류` : `${p}:+${r.added ?? 0}`)
        .join(' ');
      showToast(`동기화 완료! ${summary}`, 'success');

      // Step 3: Reload the chart with the newly updated data
      fetchData();
    } catch (err) {
      console.error('Sync failed', err);
      showToast('동기화 실패: ' + err.message, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  // --- Export Chart Data as JSON ---
  const handleExportJSON = () => {
    if (!ohclData || ohclData.length === 0) {
      showToast('내보낼 데이터가 없습니다.', 'warning');
      return;
    }

    const indicators = {};

    // Calculate enabled indicators
    if (config.ma20.enabled) {
      const maData = config.ma20.type === 'EMA'
        ? Indicators.calculateEMA(ohclData, config.ma20.period)
        : Indicators.calculateSMA(ohclData, config.ma20.period);
      indicators.ma20 = { type: config.ma20.type, period: 20, data: maData };
    }

    if (config.ma60.enabled) {
      const maData = config.ma60.type === 'EMA'
        ? Indicators.calculateEMA(ohclData, config.ma60.period)
        : Indicators.calculateSMA(ohclData, config.ma60.period);
      indicators.ma60 = { type: config.ma60.type, period: 60, data: maData };
    }

    if (config.ma200.enabled) {
      const maData = config.ma200.type === 'EMA'
        ? Indicators.calculateEMA(ohclData, config.ma200.period)
        : Indicators.calculateSMA(ohclData, config.ma200.period);
      indicators.ma200 = { type: config.ma200.type, period: 200, data: maData };
    }

    if (config.bb.enabled) {
      const bbData = Indicators.calculateBollinger(ohclData, config.bb.period, config.bb.stdDev);
      indicators.bollingerBands = {
        period: config.bb.period,
        stdDev: config.bb.stdDev,
        upper: bbData.upper,
        middle: bbData.middle,
        lower: bbData.lower
      };
    }

    if (config.vwap20.enabled) {
      const vwapData = Indicators.calculateMovingVWAP(ohclData, config.vwap20.period);
      indicators.vwap20 = { period: 20, data: vwapData };
    }

    if (config.vwap72.enabled) {
      const vwapData = Indicators.calculateMovingVWAP(ohclData, config.vwap72.period);
      indicators.vwap72 = { period: 72, data: vwapData };
    }

    if (config.tbmSuperTrend.enabled) {
      const stData = Indicators.calculateTBMSuperTrend(ohclData, config.tbmSuperTrend.period, config.tbmSuperTrend.multiplier);
      indicators.tbmSuperTrend = {
        period: config.tbmSuperTrend.period,
        multiplier: config.tbmSuperTrend.multiplier,
        data: stData
      };
    }

    if (config.rsi.enabled) {
      const rsiData = Indicators.calculateRSI(ohclData, config.rsi.period);
      indicators.rsi = { period: config.rsi.period, data: rsiData };
    }

    const exportData = {
      symbol,
      period,
      exportedAt: new Date().toISOString(),
      dataCount: ohclData.length,
      data: ohclData,
      indicators
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${symbol}_${period}_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast(`${symbol} 데이터와 지표를 JSON으로 내보냈습니다.`, 'success');
  };

  // --- 2d. Auto Refresh Polling ---
  useEffect(() => {
    let intervalId;
    if (isAutoRefresh) {
      intervalId = setInterval(() => {
        refreshData();
      }, 10000); // 10 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAutoRefresh, refreshData]);

  // --- 2b. Load More Data (Infinite Scroll) ---
  const loadMoreData = useCallback(async () => {
    // Check refs (use refs to avoid stale closures)
    if (loadingMoreRef.current || noMoreDataRef.current || ohclDataRef.current.length === 0) {
      // console.log('Skipping loadMore:', { loading: loadingMoreRef.current, noData: noMoreDataRef.current, dataLen: ohclDataRef.current.length });
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      // Get the oldest date from current data
      const currentData = ohclDataRef.current;
      if (!currentData || currentData.length === 0) return;

      const oldestDate = currentData[0].time; // YYYY-MM-DD
      console.log(`[InfiniteScroll] Loading more data before ${oldestDate}...`);

      const candles = await apiService.getStockCandles(symbol, period, 'stock', oldestDate);

      if (!candles || candles.length === 0) {
        setNoMoreData(true);
        console.log('No more historical data.');
        return;
      }

      const newCandles = transformData(candles);

      // Filter out any overlap just in case
      const nonOverlapping = newCandles.filter(c => c.time < oldestDate);

      if (nonOverlapping.length === 0) {
        setNoMoreData(true);
        return;
      }

      // Merge Data: [Oldest ... Newest]
      // Use ref for current data to ensure we have latest state
      let mergedData = [...nonOverlapping, ...ohclDataRef.current];

      // Safety: Filter nulls
      mergedData = mergedData.filter(d => d && (d.time !== undefined && d.time !== null));

      // Absolute deduplication
      const duplicateFilter = new Map();
      mergedData.forEach(item => duplicateFilter.set(item.time, item));
      mergedData = Array.from(duplicateFilter.values());

      if (mergedData.length === 0) {
        console.warn('[InfiniteScroll] Merged data is empty after filtering. Skipping update.');
        return;
      }

      mergedData.sort((a, b) => {
        const tA = typeof a.time === 'string' ? new Date(a.time + 'T00:00:00+09:00').getTime() : a.time * 1000;
        const tB = typeof b.time === 'string' ? new Date(b.time + 'T00:00:00+09:00').getTime() : b.time * 1000;
        return tA - tB;
      });

      // Update State
      setOhclData(mergedData);

      // Update Chart Series
      if (candleSeriesRef.current) {
        try {
          // Validate data shape before setting
          const invalidItem = mergedData.find(d => !d.time || d.open === undefined);
          if (invalidItem) {
            console.error('[InfiniteScroll] Invalid item found in mergedData:', invalidItem);
            throw new Error('Invalid data item in mergedData');
          }
          candleSeriesRef.current.setData(mergedData);
        } catch (e) {
          console.error('[InfiniteScroll] Failed to set data:', e);
          // Fallback or alert?
        }
      }
      if (volumeSeriesRef.current) {
        const volData = mergedData.map(d => ({
          time: d.time,
          value: d.volume,
          color: d.close >= d.open ? 'rgba(239, 83, 80, 0.5)' : 'rgba(38, 166, 154, 0.5)'
        }));
        volumeSeriesRef.current.setData(volData);
      }

      console.log(`Loaded ${nonOverlapping.length} more candles.`);

    } catch (e) {
      console.error('Load More Error:', e);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [symbol, period]); // Minimal dependencies

  // Keep loadMoreDataRef updated (Restored)
  useEffect(() => {
    loadMoreDataRef.current = loadMoreData;
  }, [loadMoreData]);


  // --- Helper: Visible Range Handler ---
  // Note: This is now largely handled by the useEffect listener, but kept for reference or direct calls if needed.
  const handleVisibleLogicalRangeChange = (range) => {
    // console.log(`[VisibleRangeDirect] from: ${range?.from}`);
    if (!range) return;
    if (range.from < 10) {
      loadMoreData();
    }
  };

  // --- Helper: Data Transformer ---
  const transformData = (candles) => {
    const formatDate = (dateStr) => {
      if (typeof dateStr === 'string') {
        if (dateStr.length === 8) {
          // Daily: 'YYYYMMDD' -> 'YYYY-MM-DD'
          return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
        } else if (dateStr.includes('T')) {
          // Intraday: 'YYYY-MM-DDTHH:mm:ss' -> Unix Timestamp
          // Lightweight Charts v4 requires Unix Timestamp (seconds) for intraday.
          // Server sends KST implicitly. To prevent offset drifting across 00:00 boundary,
          // Explicitly declare it as +09:00 so the exact seconds value represents KST.
          const date = new Date(dateStr + '+09:00');
          return Math.floor(date.getTime() / 1000);
        }
      }
      return dateStr;
    };

    const mappedCandles = candles.map(c => {
      if (!c) return null;
      return {
        time: formatDate(c.time),
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
        volume: Number(c.volume)
      };
    }).filter(item => item !== null);

    // Deep Deduplication by exact identical time
    const deduplicatedMap = new Map();
    mappedCandles.forEach(item => deduplicatedMap.set(item.time, item));
    const uniqueCandles = Array.from(deduplicatedMap.values());

    return uniqueCandles.sort((a, b) => {
      // time can be 'YYYY-MM-DD' (string) or Unix Timestamp (number)
      const tA = typeof a.time === 'string' ? new Date(a.time + 'T00:00:00+09:00').getTime() : a.time * 1000;
      const tB = typeof b.time === 'string' ? new Date(b.time + 'T00:00:00+09:00').getTime() : b.time * 1000;
      return tA - tB;
    });
  };


  // --- Helper: Update Legend ---
  const updateLegend = (data) => {
    if (!data) return;
    setLegendData({
      open: data.open,
      high: data.high,
      low: data.low,
      close: data.close,
      change: data.close - data.open,
      percent: ((data.close - data.open) / data.open) * 100
    });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  // --- 3. Indicators Management ---
  useEffect(() => {
    if (!chartRef.current || ohclData.length === 0) return;

    // Clear existing indicators
    Object.values(indicatorSeriesRefs.current).forEach(item => {
      if (item) {
        try {
          if (typeof item.remove === 'function') {
            item.remove(); // Call custom remover for PriceLines
          } else {
            chartRef.current.removeSeries(item); // Standard series removal
          }
        } catch (e) {
          console.warn("Failed to remove indicator/series", e);
        }
      }
    });
    
    // Reset refs before adding new indicators/pivot line
    indicatorSeriesRefs.current = {};

    // --- [NEW] Livermore Pivot Line Management ---
    const urlPivot = searchParams.get('pivot');
    if (candleSeriesRef.current && urlPivot) {
      const pLine = candleSeriesRef.current.createPriceLine({
        price: parseFloat(urlPivot),
        color: '#e50914',
        lineWidth: 2,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: 'Livermore Pivot',
      });
      // Store in indicator refs to ensure it gets cleared (via its own unique key)
      indicatorSeriesRefs.current['livermore_pivot'] = { 
        remove: () => {
          try {
            candleSeriesRef.current.removePriceLine(pLine);
          } catch(err) {}
        }
      };
    }

    // Helper to render Moving Averages
    const renderMA = (key) => {
      const maConfig = config[key];
      if (maConfig?.enabled) {
        try {
          const maData = maConfig.type === 'EMA'
            ? Indicators.calculateEMA(ohclData, maConfig.period)
            : Indicators.calculateSMA(ohclData, maConfig.period);

          const series = chartRef.current.addSeries(LineSeries, {
            color: maConfig.color,
            lineWidth: maConfig.lineWidth || 1,
            title: `${maConfig.type} ${maConfig.period}`,
          });

          indicatorSeriesRefs.current[key] = series;

          if (maData && maData.length > 0) {
            series.setData(maData);
          }
        } catch (e) {
          console.error(`Error setting ${key}:`, e);
        }
      }
    };

    // 1. Moving Averages
    renderMA('ma20');
    renderMA('ma60');
    renderMA('ma200');

    // 3. Bollinger Bands
    if (config.bb.enabled) {
      try {
        const bbData = Indicators.calculateBollinger(ohclData, config.bb.period, config.bb.stdDev);
        // Upper
        const upperSeries = chartRef.current.addSeries(LineSeries, { color: config.bb.color, lineWidth: config.bb.lineWidth || 1, title: 'BB Upper' });
        upperSeries.setData(bbData.upper);
        // Lower
        const lowerSeries = chartRef.current.addSeries(LineSeries, { color: config.bb.color, lineWidth: config.bb.lineWidth || 1, title: 'BB Lower' });
        lowerSeries.setData(bbData.lower);
        // Middle
        const middleSeries = chartRef.current.addSeries(LineSeries, { color: config.bb.color, lineWidth: config.bb.lineWidth || 1, lineStyle: 2, title: 'BB Mid' });
        middleSeries.setData(bbData.middle);

        // Group them to track
        indicatorSeriesRefs.current['bb_upper'] = upperSeries;
        indicatorSeriesRefs.current['bb_lower'] = lowerSeries;
        indicatorSeriesRefs.current['bb_mid'] = middleSeries;
      } catch (e) { console.error('Error setting BB:', e); }
    }

    // 4. VWAP 20
    if (config.vwap20.enabled) {
      try {
        const vwapData = Indicators.calculateMovingVWAP(ohclData, config.vwap20.period);
        const vwapSeries = chartRef.current.addSeries(LineSeries, {
          color: config.vwap20.color,
          lineWidth: config.vwap20.lineWidth || 1,
          title: `VWAP ${config.vwap20.period}`,
        });

        indicatorSeriesRefs.current['vwap20'] = vwapSeries;

        if (vwapData && vwapData.length > 0) {
          vwapSeries.setData(vwapData);
        }
      } catch (e) { console.error('Error setting VWAP20:', e); }
    }

    // 5. VWAP 72
    if (config.vwap72.enabled) {
      try {
        const vwapData = Indicators.calculateMovingVWAP(ohclData, config.vwap72.period);
        const vwapSeries = chartRef.current.addSeries(LineSeries, {
          color: config.vwap72.color,
          lineWidth: config.vwap72.lineWidth || 1,
          title: `VWAP ${config.vwap72.period}`,
        });

        indicatorSeriesRefs.current['vwap72'] = vwapSeries;

        if (vwapData && vwapData.length > 0) {
          vwapSeries.setData(vwapData);
        }
      } catch (e) { console.error('Error setting VWAP72:', e); }
    }

    // 6. Custom TBM SuperTrend
    if (config.tbmSuperTrend.enabled) {
      try {
        const rawTbmData = Indicators.calculateTBMSuperTrend(ohclData, config.tbmSuperTrend.period, config.tbmSuperTrend.multiplier);

        // Map trend to color for a single unified line
        const coloredTbmData = rawTbmData.map(d => ({
          time: d.time,
          value: d.value,
          color: d.trend === 1 ? config.tbmSuperTrend.upColor : config.tbmSuperTrend.downColor
        }));

        const stSeries = chartRef.current.addSeries(LineSeries, {
          color: config.tbmSuperTrend.upColor, // Default fallback color
          lineWidth: config.tbmSuperTrend.lineWidth || 2,
          title: `TBM SuperTrend`,
          crosshairMarkerVisible: false,
        });

        if (coloredTbmData && coloredTbmData.length > 0) stSeries.setData(coloredTbmData);

        indicatorSeriesRefs.current['tbm_supertrend'] = stSeries;
      } catch (e) { console.error('Error setting TBM SuperTrend:', e); }
    }

  }, [ohclData, config]);

  // Handlers
  const toggleConfig = (key) => {
    setConfig(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled }
    }));
  };

  const updateConfig = (key, field, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  const renderConfigRow = (label, key, isMA = false) => (
    <ToggleRow key={key} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
        <ToggleLabel>{label}</ToggleLabel>
        <Switch active={config[key].enabled} onClick={() => toggleConfig(key)} />
      </div>
      {config[key].enabled && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%', paddingLeft: '8px' }}>
          {isMA && (
            <select
              value={config[key].type || 'SMA'}
              onChange={(e) => updateConfig(key, 'type', e.target.value)}
              style={{ background: '#333', color: '#fff', border: '1px solid #444', borderRadius: '3px', fontSize: '11px', padding: '2px 4px' }}
              title="MA Type"
            >
              <option value="SMA">SMA</option>
              <option value="EMA">EMA</option>
            </select>
          )}
          <input
            type="color"
            value={config[key].color}
            onChange={(e) => updateConfig(key, 'color', e.target.value)}
            style={{ width: '24px', height: '24px', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
            title="Line Color"
          />
          <select
            value={config[key].lineWidth || 1}
            onChange={(e) => updateConfig(key, 'lineWidth', parseInt(e.target.value))}
            style={{ background: '#333', color: '#fff', border: '1px solid #444', borderRadius: '3px', fontSize: '11px', padding: '2px 4px' }}
            title="Line Width"
          >
            {[1, 2, 3, 4, 5].map(w => <option key={w} value={w}>{w}px</option>)}
          </select>
        </div>
      )}
    </ToggleRow>
  );

  const renderSuperTrendRow = (label, key) => (
    <ToggleRow key={key} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
        <ToggleLabel>{label}</ToggleLabel>
        <Switch active={config[key].enabled} onClick={() => toggleConfig(key)} />
      </div>
      {config[key].enabled && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%', paddingLeft: '8px' }}>
          <input
            type="color"
            value={config[key].upColor}
            onChange={(e) => updateConfig(key, 'upColor', e.target.value)}
            style={{ width: '20px', height: '20px', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
            title="Up Trend Color"
          />
          <input
            type="color"
            value={config[key].downColor}
            onChange={(e) => updateConfig(key, 'downColor', e.target.value)}
            style={{ width: '20px', height: '20px', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
            title="Down Trend Color"
          />
          <select
            value={config[key].lineWidth || 1}
            onChange={(e) => updateConfig(key, 'lineWidth', parseInt(e.target.value))}
            style={{ background: '#333', color: '#fff', border: '1px solid #444', borderRadius: '3px', fontSize: '11px', padding: '2px 4px' }}
            title="Line Width"
          >
            {[1, 2, 3, 4, 5].map(w => <option key={w} value={w}>{w}px</option>)}
          </select>
        </div>
      )}
    </ToggleRow>
  );

  return (
    <>
      <GlobalStyle />
      <FullScreenContainer>

        <Header>
          <TitleGroup>
            <IconButton onClick={() => navigate('/labs')}>
              <ArrowLeft size={18} />
            </IconButton>

            {isSearching ? (
              <div style={{ position: 'relative' }}>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    if (searchResults.length > 0) {
                      setSymbol(searchResults[0].code);
                      saveSearchHistory({ code: searchResults[0].code, name: searchResults[0].name });
                    } else if (/^\d{6}$/.test(searchQuery.trim())) {
                      setSymbol(searchQuery.trim());
                      saveSearchHistory({ code: searchQuery.trim(), name: searchQuery.trim() });
                    } else {
                      showToast('종목을 찾을 수 없습니다. 드롭다운에서 선택해주세요.', 'error');
                      return;
                    }
                    setIsSearching(false);
                  }
                }}>
                  <Input
                    autoFocus
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    // Use setTimeout to allow click events on the dropdown to fire before unmounting
                    onBlur={() => setTimeout(() => setIsSearching(false), 200)}
                    style={{ width: '150px', padding: '4px 8px', height: '28px' }}
                    placeholder="Search Symbol..."
                  />
                </form>
                <SearchDropdown>
                  {searchQuery.trim() ? (
                    <>
                      {isSearchingApi ? (
                        <div style={{ padding: '8px 12px', fontSize: '13px', color: '#888' }}>Searching...</div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((item, idx) => (
                          <SearchHistoryItem key={idx} onMouseDown={() => {
                            setSymbol(item.code);
                            saveSearchHistory({ code: item.code, name: item.name });
                            setIsSearching(false);
                          }}>
                            <span>{item.name} <span style={{ fontSize: '11px', color: '#777' }}>{item.code}</span></span>
                          </SearchHistoryItem>
                        ))
                      ) : (
                        <div style={{ padding: '8px 12px', fontSize: '13px', color: '#888' }}>No results found</div>
                      )}
                    </>
                  ) : (
                    <div style={{ padding: '8px 12px', fontSize: '12px', color: '#888' }}>
                      검색어를 입력하세요...
                    </div>
                  )}
                </SearchDropdown>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div onClick={() => { setIsSearching(true); setSearchQuery(symbol); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Title>{stockName || symbol}</Title>
                  <span style={{ fontSize: '11px', color: '#888' }}>{symbol} ({period})</span>
                  <Search size={14} style={{ opacity: 0.5 }} />
                </div>
                {searchHistory.length > 0 && (
                  <button
                    onClick={() => setShowHistoryModal(true)}
                    style={{ background: '#2a2a2a', border: '1px solid #444', color: '#ccc', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    최근검색어 ({searchHistory.length})
                  </button>
                )}
              </div>
            )}
          </TitleGroup>
          <Controls>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '8px', background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: '4px' }}>
              <ToggleRow style={{ marginBottom: 0 }}>
                <ToggleLabel style={{ marginRight: '6px', fontSize: '11px', color: isAutoRefresh ? '#00e676' : '#aaa' }}>Auto 10s</ToggleLabel>
                <Switch active={isAutoRefresh} onClick={() => setIsAutoRefresh(!isAutoRefresh)} />
              </ToggleRow>
              <IconButton onClick={refreshData} title="UI 데이터 새로고침 (차트 다시 그리기)">
                <RefreshCw size={14} color="#aaa" />
              </IconButton>
            </div>
            
            <button
              onClick={handleSync}
              disabled={isSyncing}
              style={{
                background: isSyncing ? '#444' : '#0072ff',
                color: '#fff',
                border: 'none',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: isSyncing ? 'not-allowed' : 'pointer'
              }}
              title="시장 데이터 동기화 (KIS에서 최신 데이터 가져오기)"
            >
              <RefreshCw size={12} style={{ animation: isSyncing ? 'spin 2s linear infinite' : 'none' }} />
              {isSyncing ? '동기화 중...' : 'Sync'}
            </button>

            <button
              onClick={handleExportJSON}
              style={{
                background: '#333',
                color: '#fff',
                border: '1px solid #555',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}
              title="현재 차트 데이터를 JSON으로 내보내기"
            >
              <Download size={12} />
              JSON
            </button>

            <IconButton onClick={() => setPanelVisible(!panelVisible)}>
              <Settings size={16} />
            </IconButton>
          </Controls>
        </Header>

        <TimeframeBar>
          {['D', 'W', 'M'].map(tf => (
            <TFButton key={tf} active={period === tf} onClick={() => setPeriod(tf)}>
              {tf}
            </TFButton>
          ))}
          {/* Intraday placeholders if backend supports */}
          {['1', '5', '15', '60', '240'].map(tf => (
            <TFButton key={tf} active={period === tf} onClick={() => setPeriod(tf)}>
              {tf === '240' ? '4h' : tf === '60' ? '1h' : `${tf}m`}
            </TFButton>
          ))}
        </TimeframeBar>

        <ChartContainer ref={chartContainerRef}>
          <Legend>
            <LegendRow>
              <LegText style={{ fontWeight: 600, marginRight: 6 }}>{symbol}</LegText>
              <LegText>O: {legendData.open.toLocaleString()}</LegText>
              <LegText>H: {legendData.high.toLocaleString()}</LegText>
              <LegText>L: {legendData.low.toLocaleString()}</LegText>
              <LegText>C: {legendData.close.toLocaleString()}</LegText>
              <LegText color={legendData.change > 0 ? '#ef5350' : legendData.change < 0 ? '#26a69a' : '#ddd'}>
                ({legendData.percent.toFixed(2)}%)
              </LegText>
            </LegendRow>
          </Legend>

          {loading && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#666' }}>
              Loading...
            </div>
          )}
          {isPending && !loading && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(0,0,0,0.8)', padding: '24px', borderRadius: '8px', zIndex: 10, textAlign: 'center', border: '1px solid #333' }}>
              <div style={{ color: '#e50914', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>최초 작업 요청중...</div>
              <div style={{ color: '#a3a3a3', fontSize: '13px' }}>서버에서 주가를 실시간으로 수집하고 있습니다. 잠시 후 다시 조회해주세요.</div>
            </div>
          )}
          {loadingMore && (
            <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', color: '#888', background: 'rgba(0,0,0,0.5)', padding: '4px 8px', borderRadius: '4px', zIndex: 10 }}>
              Loading History...
            </div>
          )}
        </ChartContainer>

        <FloatingPanel visible={panelVisible}>
          <PanelTitle>
            Chart Settings
            <IconButton onClick={() => setPanelVisible(false)}><ChevronUp size={14} /></IconButton>
          </PanelTitle>

          <ControlGroup>
            {renderConfigRow('MA 20', 'ma20', true)}
            {renderConfigRow('MA 60', 'ma60', true)}
            {renderConfigRow('MA 200', 'ma200', true)}
            {renderSuperTrendRow('TBM SuperTrend', 'tbmSuperTrend')}
            {renderConfigRow('Bollinger Bands', 'bb')}
            {renderConfigRow('VWAP 20', 'vwap20')}
            {renderConfigRow('VWAP 72', 'vwap72')}
          </ControlGroup>

          <Label>Use search to change symbol</Label>
          <div style={{ display: 'flex', gap: 4 }}>
            <Input
              placeholder="Symbol Code"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
            />
            <IconButton style={{ background: '#333' }} onClick={fetchData}>
              <Search size={14} />
            </IconButton>
          </div>

        </FloatingPanel>
      </FullScreenContainer>

      {/* Search History Modal */}
      {showHistoryModal && (
        <HistoryModalOverlay onClick={() => setShowHistoryModal(false)}>
          <HistoryModalContent onClick={e => e.stopPropagation()}>
            <HistoryHeader>
              <h3>최근 검색 종목 ({searchHistory.length}/20)</h3>
              <IconButton onClick={() => setShowHistoryModal(false)}>
                <X size={16} />
              </IconButton>
            </HistoryHeader>
            <HistoryGrid>
              {searchHistory.map((item, index) => (
                // Handle legacy strings from localStorage or new objects
                (typeof item === 'string') ? (
                  <HistoryCard key={`${item}-${index}`} onClick={() => {
                    setSymbol(item);
                    saveSearchHistory({ code: item, name: item });
                    setShowHistoryModal(false);
                  }}>
                    <span title={item}>{item}</span>
                    <DeleteHistoryBtn onClick={(e) => {
                      e.stopPropagation();
                      removeSearchHistory(e, item);
                    }}>✕</DeleteHistoryBtn>
                  </HistoryCard>
                ) : (
                  <HistoryCard key={`${item.code}-${index}`} onClick={() => {
                    setSymbol(item.code);
                    saveSearchHistory(item);
                    setShowHistoryModal(false);
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <span title={item.name} style={{ fontWeight: 600 }}>{item.name}</span>
                      <span style={{ fontSize: '11px', color: '#888' }}>{item.code}</span>
                    </div>
                    <DeleteHistoryBtn onClick={(e) => {
                      e.stopPropagation();
                      removeSearchHistory(e, item.code);
                    }}>✕</DeleteHistoryBtn>
                  </HistoryCard>
                )
              ))}
            </HistoryGrid>
          </HistoryModalContent>
        </HistoryModalOverlay>
      )}
    </>
  );
};

export default TechnicalPage;

